import { Button } from "antd";
import { LucideUpload, Search } from "lucide-react";
import {
  downloadAuditTrail,
  downloadSelectedAuditTrail,
  getAuditTrail,
} from "../../sdk/trail/trail";
import { useEffect, useState } from "react";
import Trail from "../../components/audit-trail/Trail";
import Loading from "../../shared/Loading";
import { downloadExportFile } from "../../helpers/export";
import { PERMISSIONS } from "../../contexts/permissions";
import { PermissionGate } from "../../helpers/PermissionGate";
import Calendar from "../../components/calendar/Calendar";
import {
  CheckCircle,
  XCircle,
  Clock,
  Loader,
  Check,
  AlertTriangle,
} from "lucide-react";
import Pagination from "../../shared/Pagination";
import { AuditTrailRowSkeleton } from "../../skeletons/MySkeleton";
import useAudit from "../../hooks/useAudit";

const SystemLogs = () => {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(25);
  const [fromDate, setFromDate] = useState(new Date("2023-11-23"));
  const [toDate, setToDate] = useState(new Date());
  const [customerFocusedInput, setCustomerFocusedInput] = useState(null);
  const [auditTrails, setAuditTrails] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [myLoading, setMyLoading] = useState(false);
  const [openAuditTrail, setOpenAuditTrail] = useState(false);
  const [selectedTrail, setSelectedTrail] = useState({});
  const [focusedInput, setFocusedInput] = useState(null);

  const { logAudit } = useAudit();

  // Toggle a single checkbox
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  // Toggle all checkboxes
  const toggleSelectAll = () => {
    if (selectedIds.length === auditTrails.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(auditTrails.map((log) => log.id));
    }
  };

  const handleLimitChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setLimit(newItemsPerPage);
    setPage(1);
  };

  const renderPages = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 3); i++) {
      pages.push(
        <div
          key={i}
          className={`w-[36px] cursor-pointer h-[36px] rounded-[10px] flex items-center justify-center ${
            page === i
              ? "bg-[#074073] text-white"
              : "bg-[#F5F5F5] text-[#074073]"
          }`}
          onClick={() => handlePageChange(i)}
        >
          <p>{i}</p>
        </div>,
      );
    }

    return pages;
  };

  const toggleAuditTrail = (log) => {
    setSelectedTrail(log);
    setOpenAuditTrail(true);
  };
  const closeAuditTrail = () => {
    setOpenAuditTrail(false);
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const fetchAuditTrails = async () => {
    setLoading(true);
    try {
      const response = await getAuditTrail(
        page,
        limit,
        fromDate ? fromDate.toISOString() : null,
        toDate ? toDate.toISOString() : null,
        search,
      );
      if (response.status === 200) {
        setAuditTrails(response?.data.rows);
        setPage(response?.data.page);
        setLimit(response?.data.limit);
        setTotalPages(response?.data.totalPages);
        setTotalItems(response?.data?.total);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditTrails();
  }, [page, limit, fromDate, toDate]);

  useEffect(() => {
    logAudit({
      category: "Audit Trail",
      actionCode: "VIEW_AUDIT_TRAILS",
      description: "Viewed audit trail logs",
      page: "Audit Trail",
      tab: "Audit Logs",
      section: "Audit Trail List",
      metadata: {
        route: "/dashboard/userManagement/audit-trail",
      },
    });
  }, []);

  const downloadAllAuditTrails = async () => {
    setIsLoading(true);
    try {
      const response = await downloadAuditTrail(
        page,
        limit,
        fromDate ? fromDate.toISOString() : null,
        toDate ? toDate.toISOString() : null,
        search,
        "csv",
      );
      if (response?.status === 200 || response?.status === 201) {
        downloadExportFile(response.data, `audit-trail.csv`);
        logAudit({
          category: "Audit Trail",
          actionCode: "EXPORT_AUDIT_TRAILS_CSV",
          description: "Exported audit trails as CSV",
          page: "Audit Trail",
          tab: "Audit Logs",
          section: "Export",
          metadata: {
            route: "/dashboard/system-logs",
            exportFormat: "CSV",
            exportType:
              selectedIds.length > 0 ? "Selected Records" : "All Records",
            recordsSelected: selectedIds?.length,
            fromDate: fromDate?.toISOString() ?? null,
            toDate: toDate?.toISOString() ?? null,
            search,
          },
        });
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      logAudit({
        category: "Audit Trail",
        actionCode: "EXPORT_AUDIT_TRAILS_CSV_FAILED",
        description: "Failed to export audit trails as CSV",
        page: "Audit Trail",
        tab: "Audit Logs",
        section: "Export",
        metadata: {
          route: "/dashboard/system-logs",
          exportFormat: "CSV",
          exportType:
            selectedIds.length > 0 ? "Selected Records" : "All Records",
          recordsSelected: selectedIds?.length,
          fromDate: fromDate?.toISOString() ?? null,
          toDate: toDate?.toISOString() ?? null,
          search,
          error: error?.message || "Error downloading audit trails",
        },
      });
    }
  };

  const downloadSelectedAuditTrails = async () => {
    setMyLoading(true);
    try {
      const response = await downloadSelectedAuditTrail(selectedIds, "csv");
      if (response.status === 200 || response.status === 201) {
        downloadExportFile(response.data, `audit-trail.csv`);
        setMyLoading(false);
      } else {
        setMyLoading(false);
      }
    } catch (error) {
      setMyLoading(false);
    }
  };

  function deriveActionDescription(method, route) {
    // Clean the route and extract meaningful parts
    const cleanRoute = route?.replace(/^\/api\/v\d+\//, "")?.replace(/-/g, " ");
    const parts = cleanRoute?.split("/")?.filter(Boolean); // Split into parts

    // Join meaningful parts into a phrase
    const resource = parts?.join(" ");

    // Map HTTP method to an action verb
    const methodActionMap = {
      GET: "View",
      POST: "Create",
      PUT: "Update",
      PATCH: "Modify",
      DELETE: "Delete",
    };

    const actionVerb = methodActionMap[method.toUpperCase()] || method;

    // Return the final description
    return `${actionVerb} ${resource}`;
  }

  function deriveModuleFromRoute(route) {
    // Remove '/api/' prefix and optional version like 'v1/', 'v2/', etc.
    const cleanRoute = route?.replace(/^\/api\/(v\d+\/)?/, "");

    // Split by '/' and take the first part
    const [firstSegment] = cleanRoute?.split("/").filter(Boolean);

    // Replace dashes with spaces and return as module name
    return firstSegment ? firstSegment?.replace(/-/g, " ") : "unknown";
  }

  const getStatusInfo = (statusCode) => {
    const code = Number(statusCode);

    const map = {
      200: { label: "Success", icon: CheckCircle },
      201: { label: "Created", icon: Check },
      202: { label: "Accepted", icon: Loader },
      400: { label: "Failed", icon: AlertTriangle },
      401: { label: "Unauthorized", icon: AlertTriangle },
      403: { label: "Forbidden", icon: AlertTriangle },
      404: { label: "Not Found", icon: XCircle },
      422: { label: "Rejected", icon: XCircle },
      500: { label: "Server Error", icon: AlertTriangle },
    };

    return map[code] || { label: "Unknown", icon: AlertTriangle };
  };

  return (
    <>
      {openAuditTrail && (
        <Trail closeAuditTrail={closeAuditTrail} auditData={selectedTrail} />
      )}
      <div className="w-full h-full overflow-y-auto pl-[10px]">
        <p className="font-bold mt-[10px] text-[24px] uppercase text-[#074073]">
          Audit Trails
        </p>
        <div className="w-full p-[10px]">
          <div className="flex items-center mb-[10px] border border-gray-300 rounded-md px-2 h-[40px] w-[50%] bg-white">
            <Search className="text-gray-500 w-5 h-5 mr-2" />
            <input
              type="text"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search action, users"
              className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
            />
            <button
              onClick={fetchAuditTrails}
              className="ml-2 p-2 bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <Search className="w-4 h-4 text-white" />
            </button>
          </div>
          <div className="h-[63px] mb-[10px] w-full bg-[#F7FFD7] flex items-center justify-between px-[10px]">
            <div className="flex items-center gap-[20px]">
              <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
                {selectedIds?.length} Selected
              </div>
              <div className="flex items-center gap-5">
                <p>DATE</p>
                <Calendar
                  startDate={fromDate}
                  endDate={toDate}
                  setStartDate={setFromDate}
                  setEndDate={setToDate}
                  focusedInput={customerFocusedInput}
                  setFocusedInput={setCustomerFocusedInput}
                />
              </div>
              <p className="text-[#074073] truncate text-[15px] font-normal">
                {auditTrails?.length} Results
              </p>
            </div>
            <div className="flex items-center gap-[20px]">
              <button
                className="block text-white bg-[#074073] focus:outline-none focus:ring-[#074073] rounded-[3px] text-center w-[10vw] h-[40px]"
                type="button"
                onClick={fetchAuditTrails}
              >
                <div className="flex items-center justify-center gap-2">
                  {!loading && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="29"
                      height="29"
                      viewBox="0 0 29 29"
                      fill="none"
                    >
                      <path
                        d="M14.5931 22.9583C12.232 22.9583 10.2318 22.1395 8.59252 20.5018C6.95402 18.8633 6.13477 16.8639 6.13477 14.5036C6.13477 12.1433 6.95402 10.1431 8.59252 8.50303C10.2318 6.86211 12.232 6.04166 14.5931 6.04166C16.0294 6.04166 17.3739 6.38281 18.6265 7.06511C19.88 7.74742 20.8901 8.69999 21.657 9.92282V6.04166H22.8653V12.3612H16.5458V11.1529H21.0166C20.3875 9.9486 19.5022 8.99684 18.3607 8.29761C17.2208 7.5992 15.965 7.24999 14.5931 7.24999C12.5792 7.24999 10.8674 7.95485 9.45768 9.36457C8.04796 10.7743 7.3431 12.4861 7.3431 14.5C7.3431 16.5139 8.04796 18.2257 9.45768 19.6354C10.8674 21.0451 12.5792 21.75 14.5931 21.75C16.1438 21.75 17.5434 21.3069 18.7921 20.4208C20.0407 19.5347 20.9167 18.3667 21.4202 16.9167H22.7022C22.1698 18.7259 21.1576 20.1852 19.6657 21.2944C18.1738 22.4037 16.4829 22.9583 14.5931 22.9583Z"
                        fill="white"
                      />
                    </svg>
                  )}
                  {loading && (
                    <svg
                      aria-hidden="true"
                      className="w-[20px] h-[20px] text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  )}
                  <p className="text-[#FFF] font-normal text-[16px]">REFRESH</p>
                </div>
              </button>
              <PermissionGate required={[PERMISSIONS?.UM?.EXPORT]}>
                {selectedIds?.length > 0 ? (
                  <Button
                    disabled={myLoading}
                    onClick={downloadSelectedAuditTrails}
                    className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]"
                  >
                    {myLoading ? <Loading /> : <LucideUpload size={17} />}
                    <span>Export</span>
                  </Button>
                ) : (
                  <Button
                    disabled={isLoading}
                    onClick={downloadAllAuditTrails}
                    className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]"
                  >
                    {isLoading ? <Loading /> : <LucideUpload size={17} />}
                    <span>Export</span>
                  </Button>
                )}
              </PermissionGate>
            </div>
          </div>
          <div className="w-full h-[40px] mb-[7px] text-[13px] px-[10px] bg-[#F0FFFE] flex items-center">
            <div className="w-[5%] px-[10px] text-[#000000] font-bold">
              <input
                type="checkbox"
                checked={selectedIds?.length === auditTrails?.length}
                onChange={toggleSelectAll}
              />
            </div>
            <div className="w-[15%] truncate px-[10px] text-[#000000] font-bold">
              ACTION CATEGORY
            </div>
            <div className="w-[26%] px-[10px] text-[#000000] font-bold">
              ACTION DESCRIPTION
            </div>
            <div className="w-[24%] px-[10px] text-[#000000] font-bold">
              USER
            </div>
            <div className="w-[15%] px-[10px] text-[#000000] font-bold">
              IP ADRESS
            </div>
            <div className="w-[15%] px-[10px] text-[#000000] font-bold">
              DATE/TIME
            </div>
          </div>

          {loading &&
            Array?.from({ length: 6 }).map((_, index) => (
              <AuditTrailRowSkeleton key={index} />
            ))}
          {!loading &&
            auditTrails &&
            auditTrails?.map((log) => {
              return (
                <PermissionGate
                  required={[PERMISSIONS?.UM?.VIEW_DETAILS]}
                  fallback={
                    <div className="w-full h-[37px] text-[14px] px-[10px] mb-[7px] bg-[#ffffff] flex items-center cursor-not-allowed">
                      <div className="w-[5%] px-[10px] truncate text-[#000000] font-normal">
                        <input type="checkbox" disabled />
                      </div>
                      <div className="w-[15%] px-[10px] truncate capitalize text-[#000000] font-normal">
                        {deriveModuleFromRoute(log?.route)}
                      </div>
                      <div className="w-[26%] px-[10px] truncate text-[#000000] font-normal">
                        {deriveActionDescription(log?.method, log?.route)}
                      </div>
                      <div className="w-[24%] px-[10px] truncate text-[#000000] font-normal">
                        {log?.customerId ?? log?.userId ?? "N/A"}
                        {(log?.userId || log?.customerId) && (
                          <> ({log?.userId ? "User" : "Customer"})</>
                        )}
                      </div>
                      <div className="w-[15%] px-[10px] truncate text-[#000000] font-normal">
                        {log?.ip}
                      </div>
                      <div className="w-[15%] px-[10px] truncate text-[#000000] font-normal">
                        {
                          new Date(log?.createdAt)
                            ?.toISOString()
                            ?.replace("T", " ")
                            ?.split(".")[0]
                        }
                      </div>
                    </div>
                  }
                >
                  <div
                    onClick={() => toggleAuditTrail(log)}
                    className="w-full cursor-pointer h-[37px] text-[14px] px-[10px] mb-[7px] bg-[#ffffff] flex items-center"
                  >
                    <div className="w-[5%] px-[10px] truncate text-[#000000] font-normal">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(log.id)}
                        onChange={() => toggleSelect(log.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="w-[15%] px-[10px] truncate capitalize text-[#000000] font-normal">
                      {deriveModuleFromRoute(log?.route)}
                    </div>
                    <div className="w-[26%] px-[10px] truncate text-[#000000] font-normal">
                      {deriveActionDescription(log?.method, log?.route)}
                    </div>
                    <div className="w-[24%] px-[10px] truncate text-[#000000] font-normal">
                      {log?.customerId ?? log?.userId ?? "N/A"}
                      {(log?.userId || log?.customerId) && (
                        <> ({log?.userId ? "User" : "Customer"})</>
                      )}
                    </div>
                    <div className="w-[15%] px-[10px] truncate text-[#000000] font-normal">
                      {log?.ip}
                    </div>
                    <div className="w-[15%] px-[10px] truncate text-[#000000] font-normal">
                      {
                        new Date(log?.createdAt)
                          ?.toISOString()
                          ?.replace("T", " ")
                          ?.split(".")[0]
                      }
                    </div>
                  </div>
                </PermissionGate>
              );
            })}
          {!loading && auditTrails?.length === 0 && (
            <p className="text-center text-gray-400 mt-4">
              No audit trails found
            </p>
          )}
          <div className="h-[70px] bg-white w-[97%] px-[20px] mx-auto mb-[20px] flex items-center justify-between">
            <div className="flex items-center gap-[20px]">
              <p className="text-[#074073] !text-[14px]">Rows per Page</p>
              <select
                name="page"
                id="page"
                className="w-[71px] border-[1px] px-[5px] h-[30px] focus:outline-none"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value="50">50</option>
                <option value="30">30</option>
                <option value="25">25</option>
                <option value="10">10</option>
              </select>
              <p className="text-[#074073] text-[14px]">
                {page} / {totalPages} ({totalItems} records)
              </p>
            </div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SystemLogs;
